import axios from 'axios';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';
import {
    DeliveryBoardEtaFilter,
    DeliveryBoardRow,
    DeliveryBoardSort,
    DeliveryBoardWorkingFilter,
    normalizeDeliveryBoardRows,
} from './useDeliveryBoardFeed';

export const BOARD_PAGE_STEP = 25;

export const BOARD_FILTERS: DeliveryBoardWorkingFilter[] = [
    'active',
    'part-changes',
    'backordered',
    'unfulfilled',
];

export const DELIVERY_BOARD_PATH = '/delivery/board';

export interface DeliveryBoardColumn {
    filter: DeliveryBoardWorkingFilter;
    rows: DeliveryBoardRow[];
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
    hasMore: boolean;
    loadMore: () => void;
}

interface UseDeliveryBoardDataInput {
    locationIds: Array<string> | 'all';
    etaFilters: DeliveryBoardEtaFilter[];
    sort: DeliveryBoardSort;
    search: string;
    enabled: boolean;
}

interface BoardLimits {
    'active': number;
    'part-changes': number;
    'backordered': number;
    'unfulfilled': number;
}

interface BoardColumnResponse {
    rows: DeliveryBoardRow[];
    hasMore: boolean;
}

interface BoardResponse {
    active: BoardColumnResponse;
    partChanges: BoardColumnResponse;
    backordered: BoardColumnResponse;
    unfulfilled: BoardColumnResponse;
}

const FILTER_TO_RESPONSE_KEY: Record<DeliveryBoardWorkingFilter, keyof BoardResponse> = {
    'active': 'active',
    'part-changes': 'partChanges',
    'backordered': 'backordered',
    'unfulfilled': 'unfulfilled',
};

const initialLimits = (): BoardLimits => ({
    'active': BOARD_PAGE_STEP,
    'part-changes': BOARD_PAGE_STEP,
    'backordered': BOARD_PAGE_STEP,
    'unfulfilled': BOARD_PAGE_STEP,
});

const getDeliveryBoard = async (
    sessionId: string,
    body: {
        locationIds: Array<string> | 'all';
        etaFilters: DeliveryBoardEtaFilter[];
        sort: DeliveryBoardSort;
        search: string;
        limits: BoardLimits;
    },
): Promise<BoardResponse> => {
    const { data } = await axios.post<BoardResponse>(
        `${API_URL}${DELIVERY_BOARD_PATH}`,
        body,
        { headers: { Authorization: sessionId } },
    );
    return {
        active: { rows: normalizeDeliveryBoardRows(data.active?.rows ?? []), hasMore: !!data.active?.hasMore },
        partChanges: { rows: normalizeDeliveryBoardRows(data.partChanges?.rows ?? []), hasMore: !!data.partChanges?.hasMore },
        backordered: { rows: normalizeDeliveryBoardRows(data.backordered?.rows ?? []), hasMore: !!data.backordered?.hasMore },
        unfulfilled: { rows: normalizeDeliveryBoardRows(data.unfulfilled?.rows ?? []), hasMore: !!data.unfulfilled?.hasMore },
    };
};

/**
 * Delivery Board kanban data hook.
 *
 * A single request to `POST /delivery/board`; each column is an independent
 * projection read (limit / ETA / sort) issued in parallel on the server.
 */
export const useDeliveryBoardData = ({
    locationIds,
    etaFilters,
    sort,
    search,
    enabled,
}: UseDeliveryBoardDataInput) => {
    const sessionContext = useContext(SessionContext);

    const filterContextKey = useMemo(() => [
        locationIds === 'all' ? 'all' : locationIds.join(','),
        etaFilters.join(','),
        `${sort.key}:${sort.dir}`,
        search,
    ].join('|'), [locationIds, etaFilters, sort, search]);

    // Paged-up limits are only valid for the filter context they were paged
    // in. The context key lives inside the state so the reset is synchronous:
    // on the render where the context changes, the effective limits are
    // already the defaults, and exactly one request goes out for (new
    // context, default limits). A useEffect reset would let a wasted request
    // escape first for (new context, old limits).
    const [pagedLimits, setPagedLimits] = useState<{ contextKey: string; limits: BoardLimits }>(
        () => ({ contextKey: filterContextKey, limits: initialLimits() }),
    );
    const limits = pagedLimits.contextKey === filterContextKey
        ? pagedLimits.limits
        : initialLimits();

    const query = useQuery({
        queryKey: [
            DELIVERY_BOARD_PATH,
            filterContextKey,
            limits['active'],
            limits['part-changes'],
            limits['backordered'],
            limits['unfulfilled'],
        ],
        queryFn: () => getDeliveryBoard(sessionContext?.sessionId ?? '', {
            locationIds,
            etaFilters,
            sort,
            search,
            limits,
        }),
        enabled: enabled
            && !!sessionContext?.sessionId
            && (locationIds === 'all' || locationIds.length > 0),
        refetchInterval: 1000 * 60 * 5,
        // Per-column limits are part of the key, so Load more moves to a fresh
        // cache entry. Hold the previous board so the larger page appends
        // instead of blanking all four columns — but only across limit-only
        // key changes. When the filter context (location / ETA / sort /
        // search) changed, the previous rows belong to a different view, so
        // blank the columns and let them show their loading state instead of
        // silently rendering stale data.
        placeholderData: (previousData, previousQuery) =>
            (previousQuery?.queryKey?.[1] === filterContextKey ? previousData : undefined),
    });

    const loadMoreFor = useCallback((filter: DeliveryBoardWorkingFilter) => {
        setPagedLimits((prev) => {
            const base = prev.contextKey === filterContextKey ? prev.limits : initialLimits();
            return {
                contextKey: filterContextKey,
                limits: { ...base, [filter]: base[filter] + BOARD_PAGE_STEP },
            };
        });
    }, [filterContextKey]);

    const columns: DeliveryBoardColumn[] = useMemo(() => (
        BOARD_FILTERS.map((filter) => {
            const key = FILTER_TO_RESPONSE_KEY[filter];
            const column = query.data?.[key];
            const rows = column?.rows ?? [];
            return {
                filter,
                rows,
                // Only spin a column that has nothing to show; one with rows
                // keeps them and signals the refetch through isFetching.
                isLoading: query.isLoading && rows.length === 0,
                isFetching: query.isFetching,
                isError: query.isError,
                hasMore: !!column?.hasMore,
                loadMore: () => loadMoreFor(filter),
            };
        })
    ), [query.data, query.isLoading, query.isFetching, query.isError, loadMoreFor]);

    const isAnyFetching = query.isFetching;

    const refetchAll = useCallback(() => {
        void query.refetch();
    }, [query]);

    return {
        columns,
        isAnyFetching,
        refetchAll,
    };
};
